"use client";

import Link from "next/link";
import { useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft, MapPin, Building2, Briefcase, GraduationCap,
  IndianRupee, Clock, Share2, Bookmark, CheckCircle2, ShieldCheck,
  Calendar, Users, FileText, Sparkles, Cake,
} from "lucide-react";
import { PortalShell } from "@/components/portal-chrome";
import { ApplyAction } from "./apply-action";
import { getLocalizedJob, type Job } from "@/lib/jobs";
import { useLanguage } from "@/lib/i18n/LanguageContext";

export function JobDetailsClient({ rawJob }: { rawJob: Job }) {
  const { t, lang } = useLanguage();
  const job = getLocalizedJob(rawJob, lang);
  const [saved, setSaved] = useState(false);

  const benefits =
    job.employeeBenefits && job.employeeBenefits.length > 0
      ? job.employeeBenefits
      : t.jobDetails.defaultBenefits;

  const selectionStages =
    job.selectionProcess && job.selectionProcess.length > 0
      ? job.selectionProcess
      : t.jobDetails.defaultStages;

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: `${job.title} — MMMOCL Careers`,
          url: window.location.href,
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast.success(t.jobDetails.shareCopied);
      }
    } catch {
      // User cancelled or clipboard permission denied
    }
  };

  const toggleSave = () => {
    setSaved((prev) => !prev);
    toast.success(saved ? "Removed from saved openings" : "Saved opening to bookmarks!");
  };

  return (
    <PortalShell>
      {/* Sub-header / breadcrumb */}
      <div className="border-b border-border bg-muted/40">
        <div className="mx-auto max-w-6xl px-4 py-4 sm:px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs text-muted-foreground">
            <Link href="/" className="hover:text-foreground">{t.jobDetails.breadcrumbHome}</Link>
            <span>/</span>
            <Link href="/#openings" className="hover:text-foreground">{t.jobDetails.breadcrumbOpenings}</Link>
            <span>/</span>
            <span className="font-medium text-navy">{job.id}</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="border-b border-border bg-background">
        <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8 lg:py-14">
          <Link href="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> {t.jobDetails.backToOpenings}
          </Link>
          <div className="mt-6 grid gap-8 lg:grid-cols-[1fr_auto] lg:items-start">
            <div>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="rounded-md bg-muted px-2 py-1 font-mono font-medium text-muted-foreground">{job.id}</span>
                {job.tag && (
                  <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-1 font-semibold text-primary ring-1 ring-inset ring-primary/20">
                    {job.tag}
                  </span>
                )}
                <span className="inline-flex items-center gap-1.5 rounded-full bg-success/10 px-2.5 py-1 font-semibold text-success ring-1 ring-inset ring-success/20">
                  <span className="h-1.5 w-1.5 rounded-full bg-success" /> {t.home.activelyHiring}
                </span>
              </div>
              <h1 className="mt-4 font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl lg:text-[42px] lg:leading-[1.1]">
                {job.title}
              </h1>
              <p className="mt-4 max-w-3xl text-base leading-relaxed text-muted-foreground">
                {job.desc}
              </p>
              <dl className="mt-8 grid grid-cols-2 gap-x-8 gap-y-4 text-sm sm:grid-cols-3">
                <MetaCell icon={Building2} label={t.jobDetails.deptLabel} value={job.dept} />
                <MetaCell icon={MapPin} label={t.jobDetails.locationLabel} value={job.location} />
                <MetaCell icon={Briefcase} label={t.jobDetails.typeLabel} value={job.type} />
                <MetaCell icon={GraduationCap} label={t.auth.stepEducation} value={job.exp} />
                <MetaCell icon={IndianRupee} label={t.jobDetails.salaryLabel} value={job.salary} />
                <MetaCell icon={Clock} label={t.home.applyBy} value={job.deadline} />
                {job.ageEligibility && (
                  <MetaCell icon={Cake} label={t.jobDetails.ageLabel} value={job.ageEligibility} />
                )}
              </dl>
            </div>

            <aside className="rounded-2xl border border-border bg-card p-6 shadow-[var(--shadow-elevated)] lg:sticky lg:top-24 lg:w-80">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t.jobDetails.stickyTitle}</div>
              <p className="mt-1 font-display text-lg font-bold text-navy">{job.title}</p>
              <ApplyAction
                jobId={job.id}
                label={t.home.applyNow}
                wrapperClassName="mt-5"
                buttonClassName="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-primary text-sm font-semibold text-primary-foreground shadow-[var(--shadow-soft)] transition hover:brightness-110"
                appliedBadgeClassName="inline-flex h-11 w-full items-center justify-center gap-1.5 rounded-xl border border-success/30 bg-success/10 text-sm font-semibold text-success"
                skeletonClassName="h-11 w-full"
              />
              <div className="mt-3 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={toggleSave}
                  className={`inline-flex h-10 items-center justify-center gap-1.5 rounded-lg border text-xs font-semibold transition ${
                    saved
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border bg-background text-navy hover:bg-muted"
                  }`}
                >
                  <Bookmark className={`h-3.5 w-3.5 ${saved ? "fill-primary" : ""}`} /> {saved ? "Saved" : "Save"}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex h-10 items-center justify-center gap-1.5 rounded-lg border border-border bg-background text-xs font-semibold text-navy transition hover:bg-muted"
                >
                  <Share2 className="h-3.5 w-3.5" /> {t.jobDetails.shareTitle}
                </button>
              </div>
              <ul className="mt-6 space-y-3 border-t border-border pt-5 text-xs text-muted-foreground">
                <li className="flex items-center gap-2">
                  <Calendar className="h-3.5 w-3.5 text-primary" /> {t.home.postedOn}: {job.postedDate}
                </li>
                <li className="flex items-center gap-2">
                  <ShieldCheck className="h-3.5 w-3.5 text-primary" /> {t.footer.copyright}
                </li>
              </ul>
            </aside>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="mx-auto grid max-w-6xl gap-10 px-4 py-12 sm:px-6 lg:grid-cols-[1fr_18rem] lg:px-8 lg:py-16">
        <div className="space-y-10">
          <Block title={t.jobDetails.overviewHeading} icon={Sparkles}>
            <p className="leading-relaxed">
              {job.jobDescription ||
                (lang === "mr"
                  ? `एमएमएमओसीएल मध्ये ${job.title} म्हणून, आपण मुंबईच्या नागरिकांना सुरक्षित, विश्वासार्ह आणि जागतिक दर्जाची मेट्रो सेवा पुरवण्यात महत्त्वाची भूमिका बजावाल. हे पद ${job.dept} विभागांतर्गत येते.`
                  : `As a ${job.title} at MMMOCL, you will play a critical role in delivering safe, reliable and world-class metro services to the citizens of Mumbai. This position sits within the ${job.dept} function.`)}
            </p>
          </Block>

          <Block title={t.jobDetails.responsibilitiesHeading} icon={FileText}>
            <p className="leading-relaxed">{job.desc}</p>
          </Block>

          <Block title={t.jobDetails.qualificationsHeading} icon={GraduationCap}>
            <p className="leading-relaxed">{job.exp}</p>
          </Block>

          {job.skills && (
            <Block title={t.jobDetails.skillsHeading} icon={Sparkles}>
              <p className="leading-relaxed">{job.skills}</p>
            </Block>
          )}

          <Block title={t.jobDetails.selectionHeading} icon={Users}>
            <ol className="grid gap-3 sm:grid-cols-2">
              {selectionStages.map((stage, i) => (
                <li key={`${i}-${stage}`} className="rounded-xl border border-border bg-card p-4">
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {lang === "mr" ? `टप्पा ०${i + 1}` : `Stage ${i + 1}`}
                  </div>
                  <div className="mt-1 font-semibold text-navy">{stage}</div>
                </li>
              ))}
            </ol>
          </Block>

          <div className="rounded-2xl border border-primary/20 bg-primary/5 p-6 sm:p-8">
            <div className="grid gap-5 sm:grid-cols-[1fr_auto] sm:items-center">
              <div>
                <h3 className="font-display text-xl font-bold text-navy">
                  {lang === "mr" ? "मुंबई मेट्रो सोबत काम करण्यास उत्सुक आहात?" : "Ready to join Mumbai Metro?"}
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  {lang === "mr" ? (
                    <>अर्ज करण्याची शेवटची तारीख <span className="font-semibold text-navy">{job.deadline}</span> आहे.</>
                  ) : (
                    <>Applications close on <span className="font-semibold text-navy">{job.deadline}</span>. Register once and apply to multiple openings.</>
                  )}
                </p>
              </div>
              <ApplyAction
                jobId={job.id}
                label={t.jobDetails.applyForJob}
                buttonClassName="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-primary px-5 text-sm font-semibold text-primary-foreground shadow-[var(--shadow-soft)] transition hover:brightness-110"
                appliedBadgeClassName="inline-flex h-11 items-center justify-center gap-1.5 rounded-xl border border-success/30 bg-success/10 px-5 text-sm font-semibold text-success"
                skeletonClassName="h-11 w-48"
              />
            </div>
          </div>
        </div>

        <aside className="space-y-6">
          <div className="rounded-2xl border border-border bg-card p-6">
            <div className="font-display text-base font-bold text-navy">{t.jobDetails.benefitsHeading}</div>
            <ul className="mt-4 space-y-2.5 text-sm text-muted-foreground">
              {benefits.map((b, idx) => (
                <li key={`${idx}-${b}`} className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-border bg-muted/50 p-6">
            <div className="font-display text-base font-bold text-navy">{t.header.help}</div>
            <p className="mt-2 text-sm text-muted-foreground">
              {lang === "mr"
                ? "या भरतीसंदर्भातील कोणत्याही शंकेसाठी एमएमएमओसीएल भरती विभागाशी संपर्क साधा."
                : "For queries related to this posting, contact the MMMOCL Recruitment Cell."}
            </p>
            <a href="mailto:careers@mmmocl.in" className="mt-3 inline-flex text-sm font-semibold text-primary hover:underline">
              careers@mmmocl.in
            </a>
          </div>
        </aside>
      </section>
    </PortalShell>
  );
}

function MetaCell({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <dt className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</dt>
        <dd className="mt-0.5 truncate font-semibold text-navy">{value}</dd>
      </div>
    </div>
  );
}

function Block({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="flex items-center gap-2.5">
        <div className="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
          <Icon className="h-4 w-4" />
        </div>
        <h2 className="font-display text-xl font-bold tracking-tight text-navy">{title}</h2>
      </div>
      <div className="mt-4 text-sm text-foreground/80">{children}</div>
    </section>
  );
}
